// X (Twitter) Universal Website Tag. Loaded ONLY where a pixel ID is configured
// (paid-ad traffic lands on production), so dev/staging never pull the third-party
// script. Mirrors firebase.ts: conditional init keyed on an env var, plus the
// official twq queue stub so conversions fired before uwt.js loads aren't dropped.
//
// GA4 remains the master analytics dashboard (campaign funnel is read there via
// UTM + existing events). This pixel exists purely so X Ads Manager can attribute
// signups to ad spend and report cost-per-signup.

const X_PIXEL_ID = import.meta.env.VITE_X_PIXEL_ID || '';

// Only ever load on the live site. Guards against a local prod-preview (which
// inherits .env.production) firing real test conversions into X Ads.
const LIVE_HOST = 'polymyth.quest';

type Twq = {
  (...args: unknown[]): void;
  queue?: unknown[];
  version?: string;
  // Set by uwt.js once it loads; the stub must delegate to it so calls made
  // AFTER load actually execute instead of piling up in `queue` forever.
  exe?: (...args: unknown[]) => void;
};

declare global {
  interface Window {
    twq?: Twq;
  }
}

let initialized = false;

export function isXPixelEnabled(): boolean {
  return Boolean(X_PIXEL_ID);
}

export function initXPixel(): void {
  if (
    initialized ||
    !X_PIXEL_ID ||
    typeof window === 'undefined' ||
    typeof document === 'undefined'
  )
    return;
  if (window.location.hostname !== LIVE_HOST) return;
  initialized = true;

  if (!window.twq) {
    const twq: Twq = function (...args: unknown[]) {
      // Once uwt.js installs the real executor it sets twq.exe; until then,
      // buffer calls. Matches X's official stub so post-load events fire.
      if (twq.exe) twq.exe(...args);
      else (twq.queue = twq.queue || []).push(args);
    };
    twq.version = '1.1';
    twq.queue = [];
    window.twq = twq;

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://static.ads-twitter.com/uwt.js';
    const first = document.getElementsByTagName('script')[0];
    first?.parentNode?.insertBefore(script, first);
  }

  window.twq('config', X_PIXEL_ID);
}
